import { Flex, Text } from "rebass/styled-components";
import React, {
  createContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import styled from "styled-components";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useRecoilState, useRecoilValue, useResetRecoilState } from "recoil";

import { ClipBadges } from "MVVM/Components/ClipBadge";
import { ClipSettingsButton } from "MVVM/Components/ClipSettingsModal/ClipSettingsButton";
import { useClipSettingsModal } from "MVVM/Hooks/useClipQualitySettingsModal";
import { Container } from "./styledComponents";
import { TitleAndLoadouts, NoLoadout } from "./TitleAndLoadouts";
import { Moment } from "./Moment";
import { TLoadout } from "MVVM/Views/CreateMontage/@types";
import { FinalTouches } from "./FinalTouches";
import { QuickEditViewModel } from "./ViewModel";
import { LoadingSpinner } from "../Lottie";
import theme from "theme/themeThree";
import { StyledIcon } from "MVVM/Views/Studio/RemixView/Components/Pinned";
import {
  QuickEditPreferredLoadout,
  QuickEditPreferredFinalTouches,
  QuickEditPreferredThumbnail,
  QuickEditPreferredPresetId,
  QuickEditTitle,
  QuickEditTimeRemapSelectedSong,
  QuickEditTimeRemapSongVolume,
  QuickEditTimeRemapCustomVolumeEnabled,
  QuickEditTimeRemapImpactSeverity,
  QuickEditTimeRemapCustomSeverityEnabled,
  QuickEditTimeRemapCustomSeverity,
  QuickEditTimeRemapLimitSlowmo,
  QuickEditTimeRemapUseHudSettings,
  QuickEditTimeRemapEventBreathingRoom,
  QuickEditTimeRemapSpeedupSyncEnabled,
  QuickEditTimeRemapSpeedupMaxVelocity,
  QuickEditTimeRemapSpeedupBeatFrequency,
  QuickEditTimeRemapSpeedupVfxEnabled,
  QuickEditTimeRemapApplyStudioCards,
} from "MVVM/State/quickEdit";

import {
  getMatchHistoryGameEnum,
  getPlayerGameIdByGame,
} from "MVVM/Views/MatchHistory/utils";
import { ESupportedGames } from "MVVM/@types";
import { useUser } from "MVVM/Hooks/useUser";
import { analytics } from "MVVM/Analytics";
import { canUserCreateCareerMontage as canUserCreateMontage } from "MVVM/Utilities/Montage";
import { IQuickEditMoment } from "./@types";
import {
  ProfileMontageSession,
  defaultState,
} from "MVVM/State/profileMontages";

import { ActiveAdAtom } from "MVVM/State/ads";
import { FinalizingContainer } from "./FinalizingContainer";
import { usePortal } from "MVVM/Hooks/usePortal";
import { ClipEligibilityModal } from "MVVM/Components/Modals/ClipEligibilityModal";
import { useShouldShowSubscriptionOverhaul } from "MVVM/Hooks/useSubscriptionOverhaul";
import { UnifiedUpgradeModal } from "MVVM/Components/Modals/UnifiedUpgradeModal";
import { AnimatedTooltip } from "MVVM/Components/AnimatedTooltip";
import { AdUnit } from "MVVM/Components/Ads";
import { useMediaQuery } from "MVVM/Hooks/useMediaQuery";
import { TTierKey } from "MVVM/Views/Pricing/Components/PricingCards/types";
import { addPendingClipCreation } from "MVVM/Utilities/ClipLimits/pendingClipCreations";
import { ClipQuotaBar } from "MVVM/Components/ClipQuotaBar";
import { IAmsCard, ITimeRemapParams } from "./@types";
import { FragmovieSettings } from "./FragmovieSettings";
import {
  TbAdjustments,
  TbChevronLeft,
  TbChevronRight,
  TbPalette,
  TbTypography,
} from "react-icons/tb";
import { IoCheckmark, IoLockClosed } from "react-icons/io5";
import { AiFillCloseCircle } from "react-icons/ai";
import { ReactComponent as ProIcon } from "../../../assets/images/pro/new/PRO-icon.svg";
import { getTierAccentColor } from "MVVM/Utilities/SubscriptionTiers";
import { CollapsibleSection } from "./CollapsibleSection";
import { QuickEditErrorBoundary } from "./QuickEditErrorBoundary";
import { THUMBNAIL_PREVIEW, THUMBNAIL_QUERY } from "MVVM/GraphQL/quickEdit";
import { USER_SONG } from "MVVM/GraphQL/userSong";
import { MUTATE_TOGGLE_CREATORCARD } from "MVVM/GraphQL/studioFacet";
import { hasScope } from "MVVM/Utilities/Studio";

// Context for rendering tooltips outside the scrollable area
export const TooltipPortalContext = createContext<HTMLDivElement | null>(null);
const DEFAULT_THUMBNAIL_PRESET_ID = "6061cc61028ef743a9b2b0cd";
const DEFAULT_CLIP_THUMBNAIL =
  "allstar-gaming-clip/5ed7d014f5d3001f08a17468/src/5ef431a22e77ec383c90c21c_src.png";

const getPresetIdForCard = (card: any): string => {
  if (card?.cc?.thumbnailPresetId) return card.cc.thumbnailPresetId;
  return DEFAULT_THUMBNAIL_PRESET_ID;
};

export type TQuickEditType = "MOMENT" | "MONTAGE";
export type TQuickEditFlow = "standard" | "time-remap";
interface IQuickEditProps {
  type?: TQuickEditType;
  momentId: string;
  // Additional moment ids for a multi-round (Fragmovie) request. When provided
  // and non-empty, the time-remap create sends the whole list as `ids` while
  // `momentId` remains the primary moment that drives the ViewModel queries.
  momentIds?: string[];
  // When set, the editor starts locked into this flow and the
  // Highlight/Fragmovie toggle is hidden. Used by the multi-round Fragmovie
  // entry point to force the time-remap flow.
  lockFlow?: TQuickEditFlow;
  closeModal: () => void;
  onCreate?: () => void;
}

const QuickEditInner = ({
  momentId,
  momentIds,
  lockFlow,
  closeModal,
  onCreate,
  type = "MOMENT",
}: IQuickEditProps) => {
  const { allstarUser } = useUser();
  const [flow, setFlow] = useState<TQuickEditFlow>(lockFlow ?? "standard");
  const preferredLoadout = useRecoilValue(QuickEditPreferredLoadout);
  const [selectedLoadout, setSelectedLoadout] = useState<TLoadout | null>(
    preferredLoadout || NoLoadout,
  );
  const [activeAd] = useRecoilState(ActiveAdAtom);
  const isMatchHistory = window.location.href.includes("match-history");
  const showSideAds = useMediaQuery("(min-width: 1200px)");
  const [thumbnailKey, setThumbnailKey] = useState<string>("");
  const [thumbnailUploadOpen, setThumbnailUploadOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [preferredPresetId, setPreferredPresetId] = useRecoilState(
    QuickEditPreferredPresetId,
  );
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(
    preferredPresetId,
  );
  const [preferredThumbnail, setPreferredThumbnail] = useRecoilState(
    QuickEditPreferredThumbnail,
  );
  const [title, setTitle] = useRecoilState(QuickEditTitle);
  const [finalizing, setFinalizing] = useState<boolean>(false);
  const isCreatingRef = useRef(false);
  // Shows feedback while the create request is in flight, before finalizing.
  const [requesting, setRequesting] = useState(false);
  const tooltipPortalRef = useRef<HTMLDivElement>(null);

  // Frag Movie (time-remap) persisted state
  const [persistedSong, setSelectedSong] = useRecoilState(
    QuickEditTimeRemapSelectedSong,
  );
  // The song atom rehydrates from localStorage across releases — a stale
  // shape without ui.title crashed the render, so treat it as no selection.
  const selectedSong = persistedSong?.ui?.title ? persistedSong : null;

  // A persisted CUSTOM song may have been deleted (or renamed) since it was
  // saved — without this check a deleted songId would ride into momentCreateClip
  // and pnd would silently render the fragmovie with no music. Revalidate
  // against the server: clear the selection if the song is gone, sync the title.
  const { data: customSongCheck } = useQuery(USER_SONG, {
    variables: { songId: selectedSong?._id },
    skip: !selectedSong?.isCustom || !selectedSong?._id,
    fetchPolicy: "network-only",
  });
  useEffect(() => {
    if (!selectedSong?.isCustom || !customSongCheck) return;
    const song = customSongCheck.userSong;
    if (!song || song.status !== "DONE") {
      setSelectedSong(null);
      return;
    }
    const title = song.title || song.originalFilename || "Custom song";
    if (title !== selectedSong.ui?.title) {
      setSelectedSong({
        ...selectedSong,
        ui: { ...(selectedSong.ui ?? {}), title },
      });
    }
  }, [customSongCheck, selectedSong, setSelectedSong]);
  const [songVolume, setSongVolume] = useRecoilState(
    QuickEditTimeRemapSongVolume,
  );
  const [customVolumeEnabled, setCustomVolumeEnabled] = useRecoilState(
    QuickEditTimeRemapCustomVolumeEnabled,
  );
  const [impactSeverity, setImpactSeverity] = useRecoilState(
    QuickEditTimeRemapImpactSeverity,
  );
  const [customSeverityEnabled, setCustomSeverityEnabled] = useRecoilState(
    QuickEditTimeRemapCustomSeverityEnabled,
  );
  const [customSeverity, setCustomSeverity] = useRecoilState(
    QuickEditTimeRemapCustomSeverity,
  );
  const [limitSlowmo, setLimitSlowmo] = useRecoilState(
    QuickEditTimeRemapLimitSlowmo,
  );
  const [useHudSettings, setUseHudSettings] = useRecoilState(
    QuickEditTimeRemapUseHudSettings,
  );
  const [eventBreathingRoom, setEventBreathingRoom] = useRecoilState(
    QuickEditTimeRemapEventBreathingRoom,
  );
  const [speedupSyncEnabled, setSpeedupSyncEnabled] = useRecoilState(
    QuickEditTimeRemapSpeedupSyncEnabled,
  );
  const [speedupMaxVelocity, setSpeedupMaxVelocity] = useRecoilState(
    QuickEditTimeRemapSpeedupMaxVelocity,
  );
  const [speedupBeatFrequency, setSpeedupBeatFrequency] = useRecoilState(
    QuickEditTimeRemapSpeedupBeatFrequency,
  );
  const [speedupVfxEnabled, setSpeedupVfxEnabled] = useRecoilState(
    QuickEditTimeRemapSpeedupVfxEnabled,
  );
  const [applyStudioCards, setApplyStudioCards] = useRecoilState(
    QuickEditTimeRemapApplyStudioCards,
  );
  const [showSongError, setShowSongError] = useState<boolean>(false);

  const resetSong = useResetRecoilState(QuickEditTimeRemapSelectedSong);
  const resetSongVolume = useResetRecoilState(QuickEditTimeRemapSongVolume);
  const resetCustomVolume = useResetRecoilState(
    QuickEditTimeRemapCustomVolumeEnabled,
  );
  const resetImpactSeverity = useResetRecoilState(
    QuickEditTimeRemapImpactSeverity,
  );
  const resetCustomSeverityEnabled = useResetRecoilState(
    QuickEditTimeRemapCustomSeverityEnabled,
  );
  const resetCustomSeverity = useResetRecoilState(
    QuickEditTimeRemapCustomSeverity,
  );
  const resetLimitSlowmo = useResetRecoilState(QuickEditTimeRemapLimitSlowmo);
  const resetUseHudSettings = useResetRecoilState(
    QuickEditTimeRemapUseHudSettings,
  );
  const resetEventBreathingRoom = useResetRecoilState(
    QuickEditTimeRemapEventBreathingRoom,
  );
  const resetSpeedupSyncEnabled = useResetRecoilState(
    QuickEditTimeRemapSpeedupSyncEnabled,
  );
  const resetSpeedupMaxVelocity = useResetRecoilState(
    QuickEditTimeRemapSpeedupMaxVelocity,
  );
  const resetSpeedupBeatFrequency = useResetRecoilState(
    QuickEditTimeRemapSpeedupBeatFrequency,
  );
  const resetSpeedupVfxEnabled = useResetRecoilState(
    QuickEditTimeRemapSpeedupVfxEnabled,
  );
  const resetApplyStudioCards = useResetRecoilState(
    QuickEditTimeRemapApplyStudioCards,
  );

  const resetAllTimeRemapSettings = useCallback(() => {
    resetSong();
    resetSongVolume();
    resetCustomVolume();
    resetImpactSeverity();
    resetCustomSeverityEnabled();
    resetCustomSeverity();
    resetLimitSlowmo();
    resetUseHudSettings();
    resetEventBreathingRoom();
    resetSpeedupSyncEnabled();
    resetSpeedupMaxVelocity();
    resetSpeedupBeatFrequency();
    resetSpeedupVfxEnabled();
    resetApplyStudioCards();
    setShowSongError(false);
  }, [
    resetSong,
    resetSongVolume,
    resetCustomVolume,
    resetImpactSeverity,
    resetCustomSeverityEnabled,
    resetCustomSeverity,
    resetLimitSlowmo,
    resetUseHudSettings,
    resetEventBreathingRoom,
    resetSpeedupSyncEnabled,
    resetSpeedupMaxVelocity,
    resetSpeedupBeatFrequency,
    resetSpeedupVfxEnabled,
    resetApplyStudioCards,
  ]);
  // Rendered globally by ClipQualitySettingsModalRenderer so it survives this
  // subtree unmounting (e.g. the match-history moments list reloading).
  const { open: openClipSettings } = useClipSettingsModal();
  const [showFragmoviePreview, setShowFragmoviePreview] = useState(false);
  const [isPreviewVideoPlaying, setIsPreviewVideoPlaying] = useState(false);
  const previewVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = previewVideoRef.current;
    return () => {
      if (video) {
        video.pause();
        video.src = "";
      }
    };
  }, []);

  const {
    moment,
    momentCreateClip,
    momentCreateMontage,
    mediaUploadUrl,
    fetchMediaUploadUrl,
    loadouts,
  } = QuickEditViewModel(momentId);

  const {
    portalOpen: eligibilityModalOpen,
    setPortalOpen: setEligibilityModalOpen,
    Portal: EligibilityModalPortal,
  } = usePortal(
    <ClipEligibilityModal
      surface="Quick Edit"
      clipQuota={allstarUser?.user?.clipQuota}
      userGroup={allstarUser?.user?.userGroup?.type}
      isCs2Clip={getMatchHistoryGameEnum(moment?.game) === ESupportedGames.CS2}
      onClose={() => setEligibilityModalOpen(false)}
      closeQuickEditModal={closeModal}
    />,
  );

  const showSubscriptionOverhaul = useShouldShowSubscriptionOverhaul();
  const isPremiumDisabled = showSubscriptionOverhaul
    ? allstarUser?.user?.userGroup?.type === "FREE"
    : allstarUser?.user?.userGroup?.type === "FREE" ||
      allstarUser?.user?.userGroup?.type === "ESSENTIALS";
  const allFeaturesLocked = showSubscriptionOverhaul && isPremiumDisabled;
  const essentialsAccent = allFeaturesLocked
    ? getTierAccentColor("ESSENTIALS")
    : "";
  const [paywallFeatureName, setPaywallFeatureName] = useState<string>("");
  const [paywallUpgradeAction, setPaywallUpgradeAction] = useState<string>("");
  const [paywallTierKey, setPaywallTierKey] = useState<TTierKey>("ESSENTIALS");

  const {
    portalOpen: subscriptionPaywallOpen,
    setPortalOpen: setSubscriptionPaywallOpen,
    Portal: SubscriptionPaywallPortal,
  } = usePortal(
    <UnifiedUpgradeModal
      featureName={paywallFeatureName}
      upgradeAction={paywallUpgradeAction}
      requiredTierKey={paywallTierKey}
      analyticsSource="Quick Edit"
      onClose={() => setSubscriptionPaywallOpen(false)}
    />,
  );

  const openPaywall = (
    featureName: string,
    upgradeAction: string,
    tierKey: TTierKey = "ESSENTIALS",
  ) => {
    analytics.track("Upgrade CTA Clicked", {
      location: `Quick Edit - ${featureName}`,
    });
    setPaywallFeatureName(featureName);
    setPaywallUpgradeAction(upgradeAction);
    setPaywallTierKey(tierKey);
    setSubscriptionPaywallOpen(true);
  };

  const persistedFinalTouches = useRecoilValue(QuickEditPreferredFinalTouches);
  const [selectedFinalTouches, setSelectedFinalTouches] = useState<string[]>(
    persistedFinalTouches,
  );

  const userGroup = allstarUser?.user?.userGroup?.type;
  const canCreateCareerMontage =
    type === "MONTAGE" ? canUserCreateMontage(userGroup) : false;
  const [montageSession, setMontageSession] = useRecoilState(
    ProfileMontageSession,
  );
  const onComplete = useCallback(() => {
    setTitle("");
    closeModal();
  }, [setTitle, closeModal]);

  useEffect(() => {
    fetchMediaUploadUrl();
  }, [fetchMediaUploadUrl]);

  const isOwnMoment =
    getPlayerGameIdByGame(
      getMatchHistoryGameEnum(moment?.game),
      allstarUser,
    ) === moment?.player?.gameId;

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    if (finalizing) {
      timeout = setTimeout(() => {
        onComplete();
      }, 5000);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [finalizing, onComplete]);

  useEffect(() => {
    analytics.track("Quick Edit Display", { type });
  }, [type]);

  const hasInitializedTitle = useRef(false);
  useEffect(() => {
    // A multi-moment Fragmovie shouldn't inherit the first moment's title —
    // leave the field empty so the backend names it from the whole bundle.
    // Still mark initialization so the thumbnail preview effect can run.
    if (lockFlow) {
      hasInitializedTitle.current = true;
      return;
    }
    if (moment?.title && !hasInitializedTitle.current) {
      hasInitializedTitle.current = true;
      setTitle(moment.title);
    }
  }, [moment?.title, setTitle, lockFlow]);

  const username =
    moment?.player?.user?.username || moment?.player?.displayName || "";
  const currentTitle =
    title || (lockFlow ? "Fragmovie" : moment?.title) || "Untitled Clip";
  const [debouncedTitle, setDebouncedTitle] = useState(currentTitle);
  const isDebouncing = currentTitle !== debouncedTitle;

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedTitle(currentTitle);
    }, 1500);
    return () => clearTimeout(timeout);
  }, [currentTitle]);

  const [toggleCreatorCard] = useMutation(MUTATE_TOGGLE_CREATORCARD);
  const [fetchThumbnailCards, { data: thumbnailCardsData }] = useLazyQuery(
    THUMBNAIL_QUERY,
    { fetchPolicy: "network-only" },
  );

  useEffect(() => {
    fetchThumbnailCards({
      variables: { type: ["Thumbnails"], getUserCards: true },
    });
  }, [fetchThumbnailCards]);

  const thumbnailCards = useMemo(
    () => thumbnailCardsData?.facets?.cards || [],
    [thumbnailCardsData],
  );

  useEffect(() => {
    if (thumbnailCards.length > 0 && selectedPresetId === null) {
      const activeCard = thumbnailCards.find((c: any) => c.userActivated);
      if (activeCard) {
        setSelectedPresetId(activeCard._id);
        setPreferredPresetId(activeCard._id);
      }
    }
  }, [thumbnailCards, selectedPresetId, setPreferredPresetId]);

  const selectedCard = selectedPresetId
    ? thumbnailCards.find((c: any) => c._id === selectedPresetId)
    : null;
  const selectedThumbnailPresetId = allFeaturesLocked
    ? DEFAULT_THUMBNAIL_PRESET_ID
    : selectedCard
    ? getPresetIdForCard(selectedCard)
    : DEFAULT_THUMBNAIL_PRESET_ID;

  const [displayPreview, setDisplayPreview] = useState<string | null>(null);
  const lastPreviewPresetRef = useRef<string | null>(null);

  const stripRef = useRef<HTMLDivElement>(null);
  const [showStripArrowLeft, setShowStripArrowLeft] = useState(false);
  const [showStripArrowRight, setShowStripArrowRight] = useState(false);

  const updateStripArrows = useCallback(() => {
    const el = stripRef.current;
    if (!el) return;
    setShowStripArrowLeft(el.scrollLeft > 0);
    setShowStripArrowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useLayoutEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    updateStripArrows();
    el.addEventListener("scroll", updateStripArrows);

    const ro = new ResizeObserver(updateStripArrows);
    ro.observe(el);

    const images = el.querySelectorAll("img");
    const onLoad = () => updateStripArrows();
    images.forEach((img) => img.addEventListener("load", onLoad));

    return () => {
      el.removeEventListener("scroll", updateStripArrows);
      ro.disconnect();
      images.forEach((img) => img.removeEventListener("load", onLoad));
    };
  }, [thumbnailCards, updateStripArrows, flow]);

  const scrollStrip = (direction: "left" | "right") => {
    const el = stripRef.current;
    if (!el) return;
    const scrollAmount = 90 * 3 + 4 * 3; // 3 thumbnails + gaps
    el.scrollBy({
      left: direction === "right" ? scrollAmount : -scrollAmount,
      behavior: "smooth",
    });
  };

  const [
    fetchPreview,
    { data: thumbnailPreviewData, loading: thumbnailPreviewLoading },
  ] = useLazyQuery<{ thumbnailPreview: string | null }>(THUMBNAIL_PREVIEW, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (!username || !!thumbnailKey || !hasInitializedTitle.current) return;

    lastPreviewPresetRef.current = selectedPresetId;
    fetchPreview({
      variables: {
        args: {
          clipThumbnail: DEFAULT_CLIP_THUMBNAIL,
          username,
          title: debouncedTitle,
          profileImage: moment?.player?.avatar,
        },
        presetObjId: selectedThumbnailPresetId,
      },
    });
  }, [
    username,
    debouncedTitle,
    selectedPresetId,
    thumbnailKey,
    fetchPreview,
    selectedThumbnailPresetId,
    moment?.player?.avatar,
  ]);

  const currentPreview = thumbnailPreviewData?.thumbnailPreview ?? null;

  useEffect(() => {
    if (currentPreview && selectedPresetId === lastPreviewPresetRef.current) {
      setDisplayPreview(`data:image/png;base64,${currentPreview}`);
    }
  }, [currentPreview, selectedPresetId]);

  const cardHasAccess = (card: any) => {
    if (showSubscriptionOverhaul) {
      return userGroup !== "FREE";
    }
    const scopes = card.scopeUse;
    if (!scopes || !Array.isArray(scopes) || scopes.length === 0) return true;
    if (scopes.includes("FREE")) return true;
    if (!allstarUser?.user?.userGroup?.type) return false;
    return hasScope(allstarUser.user, scopes);
  };

  const getRequiredTierForCard = (card: any): TTierKey => {
    const scopes = card.scopeUse || [];
    if (scopes.includes("CREATOR_PLAT")) return "CREATOR_PLAT";
    if (scopes.includes("PRO_PLUS")) return "CREATOR_PLAT";
    if (scopes.includes("PRO")) return "PRO";
    return "ESSENTIALS";
  };

  const handleTemplateSelect = (card: any) => {
    if (!cardHasAccess(card)) {
      const requiredTier = getRequiredTierForCard(card);
      openPaywall(
        `Thumbnail - ${card.ui?.title || "Premium Template"}`,
        "Use Premium Thumbnails",
        requiredTier,
      );
      return;
    }

    const isDeselecting = selectedPresetId === card._id;
    const newCardId = isDeselecting ? null : card._id;
    setSelectedPresetId(newCardId);
    setPreferredPresetId(newCardId);

    if (selectedPresetId) {
      toggleCreatorCard({
        variables: { cardIds: [selectedPresetId], isActive: false },
      });
    }
    if (!isDeselecting) {
      toggleCreatorCard({ variables: { cardIds: [card._id], isActive: true } });
    }
  };

  const effectsOverridesGetter = (loadoutId?: string) => {
    return {
      thumbnail: thumbnailKey ?? preferredThumbnail,
      loadout: loadoutId,
      rapid: type === "MONTAGE" && selectedFinalTouches.includes("rapidFire"),
      contextCameras: selectedFinalTouches.includes("contextCams"),
      applySignatureMusic: selectedFinalTouches.includes("music"),
      applySignatureCards: selectedFinalTouches.includes("signature"),
      disableJumpCuts: selectedFinalTouches.includes("disableJumpCuts"),
      applyMountedCameras: selectedFinalTouches.includes("mountedCameras"),
      applyFinalKillCamera: selectedFinalTouches.includes("finalKillCamera"),
      creatorCards: [
        ...(selectedPresetId ? [selectedPresetId] : []),
        ...(selectedFinalTouches.includes("hideWatermark")
          ? ["60a6d86a8c4fd555132afc97"]
          : []),
      ],
      ...(title && { title }),
    };
  };

  const getEffectsAnalytics = () => ({
    thumbnailUploaded: !!thumbnailKey,
    loadoutSelected: !!selectedLoadout?._id,
    signatureEnabled: selectedFinalTouches.includes("signature"),
    musicEnabled: selectedFinalTouches.includes("music"),
    rapidFireEnabled: selectedFinalTouches.includes("rapidFire"),
    contextCamsEnabled: selectedFinalTouches.includes("contextCams"),
    jumpCuts: !selectedFinalTouches.includes("disableJumpCuts"),
    mountedCameras: selectedFinalTouches.includes("mountedCameras"),
    finalKillCamera: selectedFinalTouches.includes("finalKillCamera"),
    titleEdited: title !== "",
  });

  const handleCreateMontage = async (loadoutId?: string) => {
    if (!canUserCreateMontage(userGroup)) return false;
    analytics.track("Montage Requested", {
      version: 2,
      clips: montageSession.clips?.length,
    });

    analytics.track("Montage Quick Edit Create", {
      game: moment?.game,
      ...getEffectsAnalytics(),
    });
    setFinalizing(true);
    await momentCreateMontage({
      variables: {
        MomentsCreateMontageInput: {
          ids: montageSession.clips.map((clip) => clip.moment!.id),
          effectsOverrides: effectsOverridesGetter(loadoutId),
        },
      },
    });
    setMontageSession(defaultState);

    return true;
  };

  const handleCreateClip = async (
    moment: IQuickEditMoment,
    loadoutId?: string,
  ): Promise<boolean> => {
    const result = await momentCreateClip({
      variables: {
        momentCreateClipInput: {
          ids: [momentId],
          matchId: moment.match.id,
          effectsOverrides: effectsOverridesGetter(loadoutId),
          checkEligibility: showSubscriptionOverhaul,
        },
      },
    });

    const errors = result?.data?.momentCreateClip?.errors;
    const eligibilityError = errors?.find(
      (e: any) => e.field === "eligibility",
    );
    if (eligibilityError) {
      setEligibilityModalOpen(true);
      isCreatingRef.current = false;
      return false;
    }

    addPendingClipCreation();

    analytics.track("Quick Edit Create", getEffectsAnalytics());

    setFinalizing(true);
    return true;
  };

  // Reports whether a clip was created, so a refusal never reads as one.
  const handleCreateTimeRemap = async (
    params: ITimeRemapParams,
  ): Promise<boolean> => {
    if (!moment) return false;

    analytics.track("Time Remap Create", {
      applyStudioCards: params.applyStudioCards,
      customSeverityEnabled: params.customSeverityEnabled,
      customVolumeEnabled: params.customVolumeEnabled,
      eventBreathingRoom: params.eventBreathingRoom,
      hasSong: !!params.selectedSong,
      impactSeverity: params.impactSeverity,
      limitSlowmo: params.limitSlowmo,
      useHudSettings: params.useHudSettings,
      songVolume: params.songVolume,
      speedupBeatFrequency: params.speedupBeatFrequency,
      speedupSyncEnabled: params.speedupSyncEnabled,
      speedupVfxEnabled: params.speedupVfxEnabled,
      titleEdited: params.title !== "",
      momentCount: momentIds && momentIds.length ? momentIds.length : 1,
    });

    const overrides = effectsOverridesGetter();

    try {
      const result = await momentCreateClip({
        variables: {
          momentCreateClipInput: {
            ids: momentIds && momentIds.length ? momentIds : [momentId],
            matchId: moment.match.id,
            effectsOverrides: {
              ...(params.title && { title: params.title }),
              ...(overrides.thumbnail && { thumbnail: overrides.thumbnail }),
              ...(overrides.creatorCards &&
                overrides.creatorCards.length > 0 && {
                  creatorCards: overrides.creatorCards,
                }),
            },
            timeRemapParams: {
              applyStudioCards: params.applyStudioCards,
              remapConfig: {
                severity: params.impactSeverity,
              },
              ...(params.selectedSong &&
                (params.selectedSong.isCustom
                  ? { customSongId: params.selectedSong._id }
                  : { amsCard: params.selectedSong._id })),
              ...(params.customVolumeEnabled && {
                amsMixWeight: params.songVolume,
              }),
              ...(params.customSeverityEnabled && {
                customSeverity: params.customSeverity,
              }),
              limitSlowmo: params.limitSlowmo,
              useHudSettings: params.useHudSettings,
              eventBreathingRoom: params.eventBreathingRoom,
              ...(params.speedupSyncEnabled && {
                speedupSync: {
                  maxVelocity: params.speedupMaxVelocity,
                  beatFrequency: params.speedupBeatFrequency,
                },
              }),
              speedupVfxSelection: params.speedupVfxEnabled ? "flash" : null,
            },
          },
        },
      });

      const errors = result?.data?.momentCreateClip?.errors;
      const eligibilityError = errors?.find(
        (e: any) => e.field === "eligibility",
      );
      if (eligibilityError) {
        setEligibilityModalOpen(true);
        isCreatingRef.current = false;
        setIsCreatingFragmovie(false);
        return false;
      }

      addPendingClipCreation();

      setFinalizing(true);
      return true;
    } catch {
      isCreatingRef.current = false;
      setIsCreatingFragmovie(false);
      return false;
    }
  };

  const [isCreatingFragmovie, setIsCreatingFragmovie] = useState(false);

  const handleCreateTimeRemapMerged = async () => {
    if (!selectedSong) {
      setShowSongError(true);
      return;
    }
    setShowSongError(false);

    if (allFeaturesLocked) {
      analytics.track("Time Remap - Paywall Shown", {
        location: "Quick Edit - Time Remap",
      });
      openPaywall("Instant Fragmovie", "Create Instant Fragmovies");
      return;
    }

    if (isCreatingRef.current) return;
    isCreatingRef.current = true;
    setIsCreatingFragmovie(true);

    // A custom title is sent through untouched — no " - Fragmovie" suffix.
    // The field pre-fills with the moment title, so an unedited or empty
    // title sends "" and the backend builds its "... - Fragmovie" default
    // from the moment(s).
    const fragmovieTitle = title && title !== moment?.title ? title : "";

    const created = await handleCreateTimeRemap({
      title: fragmovieTitle,
      selectedSong,
      songVolume,
      customVolumeEnabled,
      impactSeverity: impactSeverity / 100,
      customSeverityEnabled,
      customSeverity,
      limitSlowmo,
      eventBreathingRoom,
      speedupSyncEnabled,
      speedupMaxVelocity,
      speedupBeatFrequency,
      speedupVfxEnabled,
      applyStudioCards,
      useHudSettings,
    });

    if (created) onCreate?.();
  };

  const handleClick = async (moment: IQuickEditMoment) => {
    if (!loadouts) return;
    if (isCreatingRef.current) return;
    isCreatingRef.current = true;
    setRequesting(true);
    try {
      await createWithLoadout(moment);
    } catch (error) {
      // Otherwise the button looks ready but ignores every later click.
      isCreatingRef.current = false;
      throw error;
    } finally {
      setRequesting(false);
    }
  };

  const createWithLoadout = async (moment: IQuickEditMoment) => {
    if (!loadouts) return;
    let loadoutId = selectedLoadout?._id;
    if (selectedLoadout?.name === "Surprise Me") {
      const randomLoadout =
        loadouts[Math.floor(Math.random() * loadouts.length)];
      loadoutId = randomLoadout._id;
    }
    if (selectedLoadout?.name === "No Loadout") {
      loadoutId = "NO_LOADOUT";
    }

    if (type === "MOMENT") {
      const success = await handleCreateClip(moment, loadoutId);
      if (!success) return;
      if (!isOwnMoment && isMatchHistory) closeModal();
    } else {
      const success = await handleCreateMontage(loadoutId);
      if (!success) {
        isCreatingRef.current = false;
        return;
      }
    }

    setPreferredThumbnail(thumbnailKey);

    onCreate?.();
  };

  const fallbackBackground =
    moment?.match?.hero ||
    `${process.env.REACT_APP_S3_CLIP_BUCKET}/static/profile/upsell-modal/banner.png`;

  const renderThumbnailPreview = () => {
    if (thumbnailKey) {
      return <ThumbnailHeaderImage src={thumbnailKey} alt="Custom thumbnail" />;
    }

    if ((thumbnailPreviewLoading || isDebouncing) && !currentPreview) {
      return (
        <ThumbnailLoadingState
          $backgroundUrl={displayPreview || fallbackBackground}
          $isImage={!!displayPreview}
        >
          <LoadingSpinner height={20} width={20} />
          <ThumbnailLoadingText>Loading preview...</ThumbnailLoadingText>
        </ThumbnailLoadingState>
      );
    }

    if (currentPreview) {
      return (
        <ThumbnailHeaderImage
          src={`data:image/png;base64,${currentPreview}`}
          alt="Thumbnail preview"
        />
      );
    }

    if (selectedCard?.ui?.image) {
      return (
        <ThumbnailHeaderImage
          src={`${process.env.REACT_APP_S3_STATIC}/static/${selectedCard.ui.image}`}
          alt={selectedCard.ui.title}
        />
      );
    }

    return (
      <ThumbnailLoadingState
        $backgroundUrl={fallbackBackground}
        $isImage={false}
      >
        <LoadingSpinner height={20} width={20} />
        <ThumbnailLoadingText>Loading preview...</ThumbnailLoadingText>
      </ThumbnailLoadingState>
    );
  };

  if (!moment)
    return (
      <Container
        alignItems={"center"}
        justifyContent={"center"}
        minHeight={650}
      >
        <LoadingSpinner height={25} width={25} />
      </Container>
    );
  if (finalizing || activeAd === "clip-modal") {
    return (
      <FinalizingContainer
        finalizing={finalizing}
        loadingAd={false}
        activeAd={activeAd}
        closeModal={closeModal}
      />
    );
  }

  return (
    <>
      <Container minHeight={300} flexDirection={"column"}>
        <ThumbnailCarousel>
          <ThumbnailPreviewArea>
            {renderThumbnailPreview()}
            {type === "MOMENT" && (
              <MomentOverlay>
                <Moment moment={moment} hideTimelineLabel compact />
              </MomentOverlay>
            )}
          </ThumbnailPreviewArea>
          <CarouselBar>
            <StripArrow
              $hidden={!showStripArrowLeft}
              onClick={() => scrollStrip("left")}
            >
              <TbChevronLeft size={18} />
            </StripArrow>
            <TemplateStripContainer ref={stripRef}>
              <UploadStripItem
                $isActive={!!thumbnailKey}
                onClick={() => {
                  setThumbnailUploadOpen(false);
                  requestAnimationFrame(() => setThumbnailUploadOpen(true));
                }}
              >
                {thumbnailKey ? (
                  <>
                    <TemplateStripImage
                      src={thumbnailKey}
                      alt="Custom thumbnail"
                    />
                    <UploadStripRemove
                      onClick={(e) => {
                        e.stopPropagation();
                        setThumbnailKey("");
                        setPreferredThumbnail("");
                      }}
                    >
                      <AiFillCloseCircle size={14} />
                    </UploadStripRemove>
                  </>
                ) : (
                  <UploadStripLabel>Upload</UploadStripLabel>
                )}
              </UploadStripItem>
              {thumbnailCards.map((card: any) => {
                const locked = !cardHasAccess(card);
                return (
                  <TemplateStripItem
                    key={card._id}
                    $isSelected={selectedPresetId === card._id}
                    $locked={locked}
                    onClick={() => handleTemplateSelect(card)}
                  >
                    <TemplateStripImage
                      src={`${process.env.REACT_APP_S3_STATIC}/static/${card.ui?.image}`}
                      alt={card.ui?.title}
                      $locked={locked}
                    />
                    {locked && (
                      <TemplateStripLock>
                        <IoLockClosed size={14} />
                      </TemplateStripLock>
                    )}
                  </TemplateStripItem>
                );
              })}
            </TemplateStripContainer>
            <StripArrow
              $hidden={!showStripArrowRight}
              onClick={() => scrollStrip("right")}
            >
              <TbChevronRight size={18} />
            </StripArrow>
          </CarouselBar>
        </ThumbnailCarousel>
        {getMatchHistoryGameEnum(moment?.game) === ESupportedGames.CS2 &&
          type === "MOMENT" &&
          !lockFlow && (
            <>
              <EditingStyleSection>
                <EditingStyleHeader>
                  <EditingStyleLabel>Editing Style</EditingStyleLabel>
                  {flow === "time-remap" && (
                    <WhatIsThisLink
                      onClick={() => setShowFragmoviePreview(true)}
                    >
                      What is Instant Fragmovie?
                    </WhatIsThisLink>
                  )}
                </EditingStyleHeader>
                <ModeSliderTrack
                  $isFragmovie={flow === "time-remap"}
                  onClick={() => {
                    if (flow === "standard") {
                      analytics.track("Time Remap Flow Started", {
                        source: "toggle",
                      });
                      setFlow("time-remap");
                    } else {
                      setFlow("standard");
                      setExpandedSection(
                        expandedSection === "fragmovie"
                          ? null
                          : expandedSection,
                      );
                    }
                  }}
                >
                  <ModeSliderThumb $isFragmovie={flow === "time-remap"} />
                  <ModeSliderLabel $active={flow === "standard"}>
                    Highlight
                  </ModeSliderLabel>
                  <ModeSliderLabel $active={flow === "time-remap"}>
                    Fragmovie
                  </ModeSliderLabel>
                </ModeSliderTrack>
              </EditingStyleSection>
            </>
          )}
        {showFragmoviePreview && (
          <FragmoviePreviewModal>
            <FragmoviePreviewContent>
              <FragmoviePreviewClose
                onClick={() => {
                  setShowFragmoviePreview(false);
                  setIsPreviewVideoPlaying(false);
                  if (previewVideoRef.current) {
                    previewVideoRef.current.pause();
                    previewVideoRef.current.currentTime = 0;
                  }
                }}
              >
                ×
              </FragmoviePreviewClose>
              <Text variant="text.headingXSmall" mb={3}>
                What is Instant Fragmovie
              </Text>
              <FragmoviePreviewVideoWrap>
                <FragmoviePreviewVideo
                  ref={previewVideoRef}
                  src={`${process.env.REACT_APP_S3_STATIC}/static/web/whatIsTimeRemap.mp4`}
                  loop
                  playsInline
                  onClick={() => {
                    if (previewVideoRef.current) {
                      if (previewVideoRef.current.paused) {
                        previewVideoRef.current.play();
                        setIsPreviewVideoPlaying(true);
                      } else {
                        previewVideoRef.current.pause();
                        setIsPreviewVideoPlaying(false);
                      }
                    }
                  }}
                />
                {!isPreviewVideoPlaying && (
                  <FragmoviePlayOverlay
                    onClick={() => {
                      if (previewVideoRef.current) {
                        previewVideoRef.current.play();
                        setIsPreviewVideoPlaying(true);
                      }
                    }}
                  >
                    ▶
                  </FragmoviePlayOverlay>
                )}
              </FragmoviePreviewVideoWrap>
              <Text
                variant="text.labelSmall"
                color={theme.colors.chalk}
                style={{ lineHeight: 1.5 }}
              >
                Instant Fragmovie creates a cinematic edit of your clip with
                time warping effects synced to music. The clip will slow down
                during key moments and speed up between them, creating a
                professional fragmovie look with minimal effort.
              </Text>
            </FragmoviePreviewContent>
          </FragmoviePreviewModal>
        )}
        <HeaderContent>
          {type === "MONTAGE" && (
            <Text
              variant="text.labelSmall"
              color={theme.colors.ice}
              style={{ fontWeight: 700 }}
            >
              Customize Your Montage
            </Text>
          )}
          <SectionLabel>
            <SectionIcon>
              <TbTypography />
            </SectionIcon>
            Title
          </SectionLabel>
          <TitleInput
            value={title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setTitle(e.target.value)
            }
            placeholder="Enter a custom clip title, or we'll create one for you."
          />
          <ClipBadges
            tags={moment?.clipTags}
            size="sm"
            maxVisible={4}
            expandable
          />
        </HeaderContent>
        <FinalTouches
          setThumbnailKey={(key) => {
            setThumbnailKey(key);
            setThumbnailUploadOpen(false);
            fetchMediaUploadUrl();
          }}
          selectedFinalTouches={selectedFinalTouches}
          setSelectedFinalTouches={setSelectedFinalTouches}
          mediaUploadUrl={mediaUploadUrl}
          isOwnMoment={isOwnMoment}
          moment={moment}
          closeModal={closeModal}
          section="thumbnail"
          openThumbnailUpload={thumbnailUploadOpen}
          type={type}
        />
        <TooltipPortalContext.Provider value={tooltipPortalRef.current}>
          <ScrollableSection>
            {getMatchHistoryGameEnum(moment?.game) === ESupportedGames.CS2 && (
              <FinalTouches
                setThumbnailKey={setThumbnailKey}
                selectedFinalTouches={selectedFinalTouches}
                setSelectedFinalTouches={setSelectedFinalTouches}
                mediaUploadUrl={mediaUploadUrl}
                isOwnMoment={isOwnMoment}
                moment={moment}
                closeModal={closeModal}
                section="cs2hud"
                type={type}
              />
            )}
            {flow === "standard" ? (
              <CollapsibleSection
                title="Advanced Effects"
                icon={<TbAdjustments />}
                expanded={expandedSection === "effects"}
                onToggle={() =>
                  setExpandedSection(
                    expandedSection === "effects" ? null : "effects",
                  )
                }
                locked={allFeaturesLocked}
                lockedAccentColor={essentialsAccent}
                lockedTierLabel="Basic"
                onLockedClick={() =>
                  openPaywall("Advanced Effects", "Use Advanced Effects")
                }
                lockedContent={
                  <LockedUpsellRow>
                    <LockedUpsellFeatures>
                      <LockedFeatureChip>
                        <IoCheckmark size={12} color={essentialsAccent} />
                        Camera effects
                      </LockedFeatureChip>
                      <LockedFeatureChip>
                        <IoCheckmark size={12} color={essentialsAccent} />
                        Custom effects
                      </LockedFeatureChip>
                      <LockedFeatureChip>
                        <IoCheckmark size={12} color={essentialsAccent} />
                        Hide watermark
                      </LockedFeatureChip>
                    </LockedUpsellFeatures>
                  </LockedUpsellRow>
                }
              >
                <FinalTouches
                  setThumbnailKey={setThumbnailKey}
                  selectedFinalTouches={selectedFinalTouches}
                  setSelectedFinalTouches={setSelectedFinalTouches}
                  mediaUploadUrl={mediaUploadUrl}
                  isOwnMoment={isOwnMoment}
                  moment={moment}
                  closeModal={closeModal}
                  section="effects"
                  type={type}
                />
              </CollapsibleSection>
            ) : (
              <>
                <CollapsibleSection
                  title="Fragmovie Settings"
                  icon={<TbAdjustments />}
                  expanded={expandedSection === "fragmovie"}
                  onToggle={() =>
                    setExpandedSection(
                      expandedSection === "fragmovie" ? null : "fragmovie",
                    )
                  }
                >
                  <FragmovieSettings
                    onReset={resetAllTimeRemapSettings}
                    selectedSong={selectedSong}
                    setSelectedSong={setSelectedSong}
                    songVolume={songVolume}
                    setSongVolume={setSongVolume}
                    customVolumeEnabled={customVolumeEnabled}
                    setCustomVolumeEnabled={setCustomVolumeEnabled}
                    impactSeverity={impactSeverity}
                    setImpactSeverity={setImpactSeverity}
                    customSeverityEnabled={customSeverityEnabled}
                    setCustomSeverityEnabled={setCustomSeverityEnabled}
                    customSeverity={customSeverity}
                    setCustomSeverity={setCustomSeverity}
                    limitSlowmo={limitSlowmo}
                    setLimitSlowmo={setLimitSlowmo}
                    useHudSettings={useHudSettings}
                    setUseHudSettings={setUseHudSettings}
                    eventBreathingRoom={eventBreathingRoom}
                    setEventBreathingRoom={setEventBreathingRoom}
                    speedupSyncEnabled={speedupSyncEnabled}
                    setSpeedupSyncEnabled={setSpeedupSyncEnabled}
                    speedupMaxVelocity={speedupMaxVelocity}
                    setSpeedupMaxVelocity={setSpeedupMaxVelocity}
                    speedupBeatFrequency={speedupBeatFrequency}
                    setSpeedupBeatFrequency={setSpeedupBeatFrequency}
                    speedupVfxEnabled={speedupVfxEnabled}
                    setSpeedupVfxEnabled={setSpeedupVfxEnabled}
                    applyStudioCards={applyStudioCards}
                    setApplyStudioCards={setApplyStudioCards}
                    showSongError={showSongError}
                    setShowSongError={setShowSongError}
                  />
                </CollapsibleSection>
              </>
            )}
            <CollapsibleSection
              title={flow === "time-remap" ? "Overlay" : "Signature & Loadout"}
              icon={<TbPalette />}
              expanded={expandedSection === "signature"}
              onToggle={() =>
                setExpandedSection(
                  expandedSection === "signature" ? null : "signature",
                )
              }
              locked={allFeaturesLocked}
              lockedAccentColor={essentialsAccent}
              lockedTierLabel="Basic"
              onLockedClick={() =>
                openPaywall(
                  flow === "time-remap" ? "Overlay" : "Signature & Thumbnail",
                  flow === "time-remap"
                    ? "Use Overlays"
                    : "Use Signatures & Thumbnails",
                )
              }
              lockedContent={
                <LockedUpsellRow>
                  <LockedUpsellFeatures>
                    <LockedFeatureChip>
                      <IoCheckmark size={12} color={essentialsAccent} />
                      Custom thumbnails
                    </LockedFeatureChip>
                    {flow !== "time-remap" && (
                      <>
                        <LockedFeatureChip>
                          <IoCheckmark size={12} color={essentialsAccent} />
                          Signature intro/outro
                        </LockedFeatureChip>
                        <LockedFeatureChip>
                          <IoCheckmark size={12} color={essentialsAccent} />
                          Signature music
                        </LockedFeatureChip>
                      </>
                    )}
                  </LockedUpsellFeatures>
                </LockedUpsellRow>
              }
            >
              {flow !== "time-remap" && (
                <TitleAndLoadouts
                  moment={moment}
                  selectedLoadout={selectedLoadout}
                  setSelectedLoadout={setSelectedLoadout}
                  setTitle={setTitle}
                  title={title}
                  loadouts={loadouts ?? []}
                  type={type}
                  section="loadout"
                  flow={flow}
                />
              )}
              {flow === "time-remap" ? (
                <FinalTouches
                  setThumbnailKey={setThumbnailKey}
                  selectedFinalTouches={selectedFinalTouches}
                  setSelectedFinalTouches={setSelectedFinalTouches}
                  mediaUploadUrl={mediaUploadUrl}
                  isOwnMoment={isOwnMoment}
                  moment={moment}
                  closeModal={closeModal}
                  section="overlay"
                  type={type}
                />
              ) : (
                <FinalTouches
                  setThumbnailKey={setThumbnailKey}
                  selectedFinalTouches={selectedFinalTouches}
                  setSelectedFinalTouches={setSelectedFinalTouches}
                  mediaUploadUrl={mediaUploadUrl}
                  isOwnMoment={isOwnMoment}
                  moment={moment}
                  closeModal={closeModal}
                  section="signature"
                  type={type}
                />
              )}
            </CollapsibleSection>
          </ScrollableSection>
          <TooltipPortalTarget ref={tooltipPortalRef} />
        </TooltipPortalContext.Provider>
        <Controls>
          <CancelButton onClick={() => closeModal()}>Cancel</CancelButton>
          <ControlsRight>
            {type !== "MONTAGE" && (
              <AnimatedTooltip
                content="Set your defaults for every clip: Autosave, clip quality, and your Counter-Strike HUD."
                position="top"
                maxWidth={250}
              >
                <ClipSettingsButton onClick={() => openClipSettings()} />
              </AnimatedTooltip>
            )}
            {type === "MONTAGE" && !canCreateCareerMontage ? (
              <CreateButton
                onClick={() =>
                  openPaywall("Career Montage", "Create Career Montages")
                }
              >
                Create Clip
              </CreateButton>
            ) : flow === "time-remap" ? (
              <FragmovieCreateWrap>
                <FragmovieCreateButton
                  disabled={finalizing || isCreatingFragmovie}
                  onClick={() => handleCreateTimeRemapMerged()}
                >
                  {isCreatingFragmovie ? (
                    <LoadingSpinner height={16} width={16} />
                  ) : allFeaturesLocked ? (
                    <>
                      Create Instant Fragmovie <ProBadgeIcon />
                    </>
                  ) : (
                    "Create Instant Fragmovie"
                  )}
                </FragmovieCreateButton>
                {showSongError && (
                  <CreateErrorText>Please select a song first</CreateErrorText>
                )}
              </FragmovieCreateWrap>
            ) : moment?.clip?.id ? (
              <RemixButton
                disabled={finalizing || requesting}
                onClick={() => handleClick(moment)}
              >
                {requesting ? (
                  <LoadingSpinner height={16} width={16} />
                ) : (
                  <>
                    <StyledIcon />
                    Remix
                  </>
                )}
              </RemixButton>
            ) : (
              <CreateButton
                disabled={finalizing || requesting}
                onClick={() => handleClick(moment)}
              >
                {requesting ? (
                  <LoadingSpinner height={16} width={16} />
                ) : (
                  "Create Clip"
                )}
              </CreateButton>
            )}
          </ControlsRight>
        </Controls>
        {eligibilityModalOpen && EligibilityModalPortal}
        {subscriptionPaywallOpen && SubscriptionPaywallPortal}
      </Container>
      {showSideAds && (
        <>
          <SideAdOverlay $position="left">
            <AdUnit
              key="quick-edit-left-skyscraper"
              slotId="quick-edit-left-skyscraper"
              adUnitType="large-skyscraper"
              collapseIfEmpty
              showUpgradeCta
            />
          </SideAdOverlay>
          <SideAdOverlay $position="right">
            <AdUnit
              key="quick-edit-right-skyscraper"
              slotId="quick-edit-right-skyscraper"
              adUnitType="large-skyscraper"
              collapseIfEmpty
              showUpgradeCta
            />
          </SideAdOverlay>
        </>
      )}
    </>
  );
};

export const QuickEdit = (props: IQuickEditProps) => (
  <QuickEditErrorBoundary>
    <QuickEditInner {...props} />
  </QuickEditErrorBoundary>
);

const TitleInput = styled.input`
  background-color: ${({ theme }) => theme.colors.navy400};
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  border-radius: 8px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.shade100};
  font-family: Barlow, sans-serif;
  font-size: 14px;
  font-weight: 500;
  outline: none;
  padding: 12px 16px;
  transition: border-color 0.2s ease;
  width: 100%;

  &::placeholder {
    color: ${({ theme }) => theme.colors.shade400};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.envy};
  }
`;

const ScrollableSection = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  width: 100%;
`;

const TooltipPortalTarget = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 100;

  & > * {
    pointer-events: auto;
  }
`;

const Controls = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  max-height: 100px;
  padding: 16px 24px;
  position: relative;
`;

const CancelButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  cursor: pointer;
  font-family: Barlow, sans-serif;
  font-size: 13px;
  font-weight: 500;
  padding: 8px 12px;
  transition: color 150ms ease;

  &:hover {
    color: ${({ theme }) => theme.colors.chalk};
  }
`;

const UpgradeButton = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.spectrum};
  border: none;
  border-radius: 22px;
  color: white;
  cursor: pointer;
  display: flex;
  font-size: 13px;
  font-weight: 600;
  height: 34px;
  justify-content: center;
  padding: 0 24px;
  transition: opacity 0.2s ease;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.8;
  }
`;

const SideAdOverlay = styled.div<{ $position: "left" | "right" }>`
  position: fixed;
  top: 36%;
  ${({ $position }) => ($position === "left" ? "left: 24px;" : "right: 24px;")}
  transform: translateY(-50%);
  width: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  pointer-events: auto;

  @media (min-width: 1600px) {
    ${({ $position }) =>
      $position === "left"
        ? "left: calc(50% - 250px - 300px - 150px);"
        : "right: calc(50% - 250px - 300px - 150px);"}
  }
`;

const ThumbnailCarousel = styled.div`
  background: ${({ theme }) => theme.colors.midnight};
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  overflow: hidden;
  width: 100%;
`;

const ThumbnailPreviewArea = styled.div`
  aspect-ratio: 16 / 9;
  overflow: hidden;
  position: relative;
  width: 100%;
`;

const CarouselBar = styled(Flex)`
  align-items: center;
  background: ${({ theme }) => theme.colors.ned};
  gap: 10px;
  padding: 12px 16px;
`;

const ThumbnailHeaderImage = styled.img`
  height: 100%;
  object-fit: cover;
  width: 100%;
`;

const ThumbnailLoadingState = styled.div<{
  $backgroundUrl: string;
  $isImage: boolean;
}>`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100%;
  justify-content: center;
  overflow: hidden;
  position: relative;
  width: 100%;

  &::before {
    background-image: url(${({ $backgroundUrl }) => $backgroundUrl});
    background-position: center;
    background-size: cover;
    content: "";
    filter: ${({ $isImage }) =>
      $isImage ? "blur(12px) brightness(0.5)" : "blur(20px) brightness(0.3)"};
    inset: -20px;
    position: absolute;
    transform: scale(1.1);
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`;

const ThumbnailLoadingText = styled.span`
  color: ${({ theme }) => theme.colors.chalkAlpha40};
  font-family: Barlow, sans-serif;
  font-size: 12px;
  font-weight: 500;
`;

const TemplateStripContainer = styled.div`
  display: flex;
  flex: 1;
  gap: 4px;
  min-width: 0;
  overflow-x: auto;

  &::-webkit-scrollbar {
    height: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
    border-radius: 2px;
  }
`;

const TemplateStripItem = styled.div<{
  $isSelected: boolean;
  $locked?: boolean;
}>`
  border: 2px solid
    ${({ $isSelected, $locked, theme }) =>
      $locked
        ? theme.colors.chalkAlpha10
        : $isSelected
        ? theme.colors.envy
        : theme.colors.chalkAlpha20};
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
  height: 66px;
  overflow: hidden;
  position: relative;
  transition: border-color 150ms ease;
  width: 117px;

  &:hover {
    border-color: ${({ $isSelected, $locked, theme }) =>
      $locked
        ? theme.colors.chalkAlpha20
        : $isSelected
        ? theme.colors.envy
        : theme.colors.chalkAlpha40};
  }
`;

const TemplateStripImage = styled.img<{ $locked?: boolean }>`
  filter: ${({ $locked }) =>
    $locked ? "grayscale(100%) brightness(0.5)" : "none"};
  height: 100%;
  object-fit: cover;
  transition: filter 150ms ease;
  width: 100%;
`;

const TemplateStripLock = styled.div`
  align-items: center;
  bottom: 0;
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  display: flex;
  justify-content: center;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
`;

const UploadStripItem = styled.div<{ $isActive: boolean }>`
  align-items: center;
  border: 2px dashed
    ${({ $isActive, theme }) =>
      $isActive ? theme.colors.envy : theme.colors.chalkAlpha30};
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 66px;
  justify-content: center;
  overflow: hidden;
  transition: border-color 150ms ease;
  width: 117px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.chalkAlpha60};
  }
`;

const UploadStripLabel = styled.span`
  color: ${({ theme }) => theme.colors.chalkAlpha40};
  font-family: Barlow, sans-serif;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const UploadStripRemove = styled.button`
  align-items: center;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.alert};
  cursor: pointer;
  display: flex;
  justify-content: center;
  padding: 0;
  position: absolute;
  right: 2px;
  top: 2px;
  z-index: 1;
`;

const StripArrow = styled.button<{ $hidden?: boolean }>`
  align-items: center;
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  border: none;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 66px;
  justify-content: center;
  padding: 0;
  pointer-events: ${({ $hidden }) => ($hidden ? "none" : "auto")};
  transition: all 150ms ease;
  visibility: ${({ $hidden }) => ($hidden ? "hidden" : "visible")};
  width: 28px;

  &:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
    color: ${({ theme }) => theme.colors.chalk};
  }
`;

const MomentOverlay = styled.div`
  align-items: center;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 6px;
  bottom: 8px;
  display: flex;
  left: 8px;
  padding: 6px 10px;
  pointer-events: none;
  position: absolute;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 24px;
`;

const ControlsRight = styled(Flex)`
  align-items: center;
  gap: 12px;
`;

const CreateButton = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.envy};
  border: none;
  border-radius: 22px;
  color: white;
  cursor: pointer;
  display: flex;
  font-family: Barlow, sans-serif;
  font-size: 13px;
  font-weight: 600;
  gap: 6px;
  height: 34px;
  padding: 0 20px;
  transition: opacity 150ms ease;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const RemixButton = styled.button`
  align-items: center;
  background: linear-gradient(
    -15deg,
    rgba(46, 48, 73, 1) 0%,
    rgba(72, 51, 65, 1) 100%
  );
  border: none;
  border-radius: 22px;
  color: white;
  cursor: pointer;
  display: flex;
  font-family: Barlow, sans-serif;
  font-size: 13px;
  font-weight: 600;
  gap: 6px;
  height: 34px;
  padding: 0 20px;
  transition: background 150ms ease;
  white-space: nowrap;

  &:hover {
    background: linear-gradient(
      -15deg,
      rgba(69, 47, 87, 1) 0%,
      rgba(107, 53, 89, 1) 100%
    );
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const FragmovieCreateButton = styled.button`
  align-items: center;
  background: linear-gradient(135deg, #7b2ff7, #e63946);
  border: none;
  border-radius: 22px;
  color: #fff;
  cursor: pointer;
  display: flex;
  font-family: Barlow, sans-serif;
  font-size: 13px;
  font-weight: 600;
  gap: 6px;
  height: 34px;
  padding: 0 16px;
  transition: all 150ms ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #8b3ff7, #f04956);
    filter: brightness(1.1);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const FragmovieCreateWrap = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CreateErrorText = styled.span`
  color: ${({ theme }) => theme.colors.caution};
  font-family: Barlow, sans-serif;
  font-size: 11px;
`;

const LockedUpsellRow = styled(Flex)`
  align-items: center;
  gap: 12px;
  justify-content: space-between;
`;

const LockedUpsellFeatures = styled(Flex)`
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
`;

const LockedFeatureChip = styled.span`
  align-items: center;
  background: ${({ theme }) => theme.colors.chalkAlpha05};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  display: inline-flex;
  font-family: Barlow, sans-serif;
  font-size: 11px;
  font-weight: 500;
  gap: 4px;
  padding: 3px 8px;
`;

const SectionLabel = styled(Text).attrs({ variant: "text.labelSmall" })`
  align-items: center;
  display: flex;
  font-weight: 600;
  gap: 8px;
`;

const SectionIcon = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  display: flex;
  font-size: 16px;
`;

const EditingStyleSection = styled.div`
  padding: 12px 24px 0;
`;

const EditingStyleHeader = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const EditingStyleLabel = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow, sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.3px;
`;

const ModeSliderTrack = styled.div<{ $isFragmovie: boolean }>`
  align-items: center;
  background: ${({ theme }) => theme.colors.nedAlpha40};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 6px;
  cursor: pointer;
  display: grid;
  grid-template-columns: 1fr 1fr;
  height: 34px;
  padding: 3px;
  position: relative;
  user-select: none;
`;

const ModeSliderThumb = styled.div<{ $isFragmovie: boolean }>`
  background: ${({ theme }) => theme.colors.navy50};
  border-radius: 4px;
  bottom: 3px;
  left: 3px;
  position: absolute;
  top: 3px;
  transition: transform 250ms ease;
  width: calc(50% - 3px);
  ${({ $isFragmovie }) => $isFragmovie && "transform: translateX(calc(100%));"}
`;

const ModeSliderLabel = styled.span<{ $active: boolean }>`
  align-items: center;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.ice : theme.colors.chalkAlpha60};
  display: flex;
  font-family: Barlow, sans-serif;
  font-size: 12px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  gap: 5px;
  justify-content: center;
  position: relative;
  transition: color 250ms ease;
  z-index: 1;
`;

const ProBadgeIcon = styled(ProIcon)`
  height: 16px;
  width: 16px;
`;

const WhatIsThisLink = styled.span`
  color: ${({ theme }) => theme.colors.envy};
  cursor: pointer;
  font-family: Barlow, sans-serif;
  font-size: 12px;
  flex-shrink: 0;
  text-decoration: underline;

  &:hover {
    opacity: 0.8;
  }
`;

const FragmoviePreviewModal = styled.div`
  align-items: center;
  background: rgba(0, 0, 0, 0.8);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1000;
`;

const FragmoviePreviewContent = styled.div`
  background: ${({ theme }) => theme.colors.darkerNed};
  border-radius: 12px;
  max-width: 500px;
  padding: 24px;
  position: relative;
`;

const FragmoviePreviewClose = styled.div`
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  font-size: 24px;
  position: absolute;
  right: 12px;
  top: 12px;
`;

const FragmoviePreviewVideoWrap = styled.div`
  cursor: pointer;
  margin-bottom: 16px;
  position: relative;
  width: 100%;
`;

const FragmoviePreviewVideo = styled.video`
  border-radius: 8px;
  display: block;
  width: 100%;
`;

const FragmoviePlayOverlay = styled.div`
  align-items: center;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: flex;
  font-size: 24px;
  height: 72px;
  justify-content: center;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  transition: background 0.2s ease;
  width: 72px;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
`;
